import { useEffect, useState } from 'react';
import { Lock, TrendingDown } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { getImpact, retirerDefinitivement } from '@/shared/api/bridge';
import { nomConfirme, refCourte } from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

/**
 * Retirer un actif du périmètre d'un agrégateur : un geste DÉFINITIF (07/10/2026).
 *
 * POURQUOI UN DIALOGUE À PART, ET PLUS UNE CORBEILLE
 *
 * L'actif retiré redevient introuvable pour l'agrégateur, mais il lui RESTE attaché : il
 * ne pourra jamais être déclaré à un autre. Le geste sert à une sortie réelle (fin de
 * mandat, actif déposé), jamais à « déplacer » un équipement. D'où les deux gardes du
 * serveur, reprises ici : un motif écrit, et le nom de l'agrégateur ressaisi, comme pour
 * la coupure de ses clés. Le motif est gardé sur la déclaration et au journal d'audit.
 *
 * Ce que l'utilisateur a TAPÉ part tel quel au serveur, qui le compare à la clé et au
 * libellé du catalogue : envoyer la clé à sa place rendrait la garde serveur décorative
 * (relecture de la PR #235). La perte de revenu s'affiche avant le geste, comme pour
 * une déclaration : retirer une batterie peut faire tomber un client de 315 à 70 €/an.
 *
 * `declaration` vaut la ligne à retirer, `null` ferme le dialogue.
 */
export default function RetraitDefinitif({ agregateur, label, declaration, onFermer, surRetrait }) {
  const [motif, setMotif] = useState('');
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [impact, setImpact] = useState(null);

  // L'impact se lit à l'ouverture, jamais après le geste : annoncer la perte une fois
  // le retrait fait ne servirait qu'à faire regretter.
  useEffect(() => {
    setImpact(null);
    if (!declaration) return;
    getImpact(declaration.asset_id, agregateur, true).then(setImpact).catch(setErreur);
  }, [declaration, agregateur]);

  const fermer = () => {
    if (enCours) return;
    setMotif('');
    setSaisie('');
    setErreur(null);
    onFermer();
  };

  const retirer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await retirerDefinitivement(agregateur, declaration.asset_id, motif.trim(), saisie);
      setMotif('');
      setSaisie('');
      onFermer();
      await surRetrait?.();
    } catch (exc) {
      setErreur(exc);
    } finally {
      setEnCours(false);
    }
  };

  const pret = motif.trim() !== '' && nomConfirme(saisie, label) && !enCours;

  return (
    <Dialog open={Boolean(declaration)} onOpenChange={(o) => !o && fermer()}>
      <DialogContent className="max-w-md" data-testid="bridge-retrait-dialogue">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Lock className="h-4 w-4" aria-hidden />
            Retirer {declaration?.type_declare || 'cet actif'} ({refCourte(declaration?.ref)}) de {label} ?
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-relaxed">
            {label} ne verra plus cet actif et ne pourra plus le piloter. Il reste
            néanmoins attaché à {label} : il ne pourra jamais être déclaré à un autre
            agrégateur. Réservé à une sortie réelle : fin de mandat, actif déposé.
          </DialogDescription>
        </DialogHeader>
        {impact && (
          <p className={`flex gap-2 rounded-md px-3 py-2 text-[13px] leading-relaxed ${impact.ecart < 0
            ? 'bg-red-50 text-red-800' : 'bg-neutral-50 text-neutral-700'}`}
             data-testid="bridge-retrait-verdict">
            {impact.ecart < 0 && <TrendingDown className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
            <span>
              {impact.verdict}
              <span className="block tabular-nums text-[12px]">
                Total annuel : {impact.total_avant} € → {impact.total_apres} €
              </span>
            </span>
          </p>
        )}
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-600">
          Motif (gardé au journal)
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={2}
                    data-testid="bridge-retrait-motif" />
        </label>
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-600">
          Tapez « {label} » pour confirmer
          <Input value={saisie} onChange={(e) => setSaisie(e.target.value)}
                 data-testid="bridge-retrait-saisie" />
        </label>
        <ErreurBloc erreur={erreur} testid="bridge-retrait-erreur" />
        <DialogFooter>
          <Button size="sm" variant="outline" disabled={enCours} onClick={fermer}>Annuler</Button>
          <Button size="sm" variant="destructive" disabled={!pret} onClick={retirer}
                  data-testid="bridge-retrait-confirmer">
            Retirer définitivement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
